import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Minus, Plus, Trash2, CheckCircle2, Loader2, Clock, Truck, Store, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "../components/SectionHeading";
import api, { formatApiError } from "../lib/api";
import { IMAGES } from "../data/content";

const ADDITIONAL_SIDE_PRICE = 5;
const DESSERT_PRICE = 6;
const inputCls = "w-full min-h-[48px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-charcoal outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition";
const selCls = "w-full min-h-[48px] rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-charcoal outline-none focus:border-gold";

const PROCESS = [
  { n: 1, t: "Choose Your Package", d: "Pick 4, 8, 12, 16 meals — or a single meal / custom order." },
  { n: 2, t: "Build Each Meal", d: "Pick an entrée and up to two included sides for every meal." },
  { n: 3, t: "Submit Order Request", d: "Receive a unique Order ID — status: Pending Review." },
  { n: 4, t: "Order Review", d: "We confirm availability, logistics & final pricing." },
  { n: 5, t: "Invoice & Payment", d: "You receive a secure invoice / payment instructions." },
  { n: 6, t: "Fulfillment", d: "Sunday or Monday delivery or coordinated pickup." },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and").replace(/[^a-z0-9-]/g, "");

export default function MealPrep() {
  const [menu, setMenu] = useState({});
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({});
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [order, setOrder] = useState({
    order_type: "package",
    package_meals: null,
    meals: [{ entree: "", side1: "", side2: "", quantity: 1 }],
    additional_sides: {},
    appetizers: {},
    desserts: {},
    custom_request: "",
    fulfillment_day: "",
    fulfillment_method: "",
    delivery: { address: "", apt: "", city: "", zip: "", window: "" },
    first_name: "", last_name: "", email: "", phone: "", dietary: "", notes: "",
  });

  useEffect(() => {
    api.get("/content/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/content/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const entrees = menu["Entrées"] || [];
  const sides = menu["Sides"] || [];
  const appetizers = menu["Appetizers"] || [];
  const desserts = menu["Desserts"] || [];
  const addSidePrice = Number(settings.additional_side_price ?? ADDITIONAL_SIDE_PRICE);
  const dessertPrice = Number(settings.dessert_price ?? DESSERT_PRICE);
  const isCustom = order.order_type === "custom";
  const STEPS = ["Package", isCustom ? "Your Request" : "Build Meals", "Fulfillment", "Your Details"];

  const set = (k, v) => setOrder((p) => ({ ...p, [k]: v }));
  const setMeal = (i, patch) => setOrder((p) => ({ ...p, meals: p.meals.map((m, idx) => idx === i ? { ...m, ...patch } : m) }));
  const addMeal = () => setOrder((p) => ({ ...p, meals: [...p.meals, { entree: "", side1: "", side2: "", quantity: 1 }] }));
  const removeMeal = (i) => setOrder((p) => ({ ...p, meals: p.meals.filter((_, idx) => idx !== i) }));
  const bumpMap = (key, name, delta) => setOrder((p) => {
    const q = (p[key][name] || 0) + delta;
    const next = { ...p[key] };
    if (q <= 0) delete next[name]; else next[name] = q;
    return { ...p, [key]: next };
  });

  const additionalSidesTotal = Object.values(order.additional_sides).reduce((a, b) => a + b, 0) * addSidePrice;
  const dessertsTotal = Object.values(order.desserts).reduce((a, b) => a + b, 0) * dessertPrice;
  const selectedPackage = packages.find((p) => p.meals === order.package_meals);
  const requiredMeals = order.package_meals || 0;
  const builtCount = order.meals.reduce((a, m) => a + (m.entree ? m.quantity : 0), 0);
  const scrollToForm = () => {
    const el = document.getElementById("order");
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
  };

  const canNext = () => {
    if (step === 0) return isCustom || !!order.package_meals;
    if (step === 1) {
      if (isCustom) return order.custom_request.trim().length > 0;
      return order.meals.some((m) => m.entree) && builtCount >= requiredMeals;
    }
    if (step === 2) {
      if (!order.fulfillment_day || !order.fulfillment_method) return false;
      if (order.fulfillment_method === "Delivery")
        return order.delivery.address && order.delivery.city && order.delivery.zip;
      return true;
    }
    return true;
  };

  const next = () => {
    if (!canNext()) { toast.error("Please complete this step to continue."); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    scrollToForm();
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); scrollToForm(); };

  const buildPayload = () => ({
    first_name: order.first_name, last_name: order.last_name, email: order.email, phone: order.phone,
    order_type: order.order_type,
    package_meals: isCustom ? null : order.package_meals,
    meals: isCustom ? [] : order.meals.filter((m) => m.entree).map((m) => ({
      entree: m.entree, sides: [m.side1, m.side2].filter((s) => s && s !== "None"), quantity: m.quantity,
    })),
    additional_sides: Object.entries(order.additional_sides).map(([name, quantity]) => ({ name, quantity })),
    appetizers: Object.entries(order.appetizers).map(([name, quantity]) => ({ name, quantity })),
    desserts: Object.entries(order.desserts).map(([name, quantity]) => ({ name, quantity })),
    custom_request: order.custom_request,
    fulfillment_day: order.fulfillment_day, fulfillment_method: order.fulfillment_method,
    delivery: order.fulfillment_method === "Delivery" ? order.delivery : null,
    dietary: order.dietary, notes: order.notes,
  });

  const submit = async () => {
    if (!order.first_name || !order.last_name || !order.email || !order.phone) {
      toast.error("Please complete your contact details."); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/meal-prep-orders", buildPayload());
      setDone(data.order_id);
      scrollToForm();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="meal-prep-page" className="bg-bone">
      {/* HERO */}
      <section className="relative pt-28 lg:pt-36 pb-16 overflow-hidden">
        <img src={IMAGES.mealPrep2} alt="Meal prep" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow text-gold mb-3">Weekly Meal Prep</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-white text-balance max-w-3xl">
            Your Week Just Got More Flavorful.
          </h1>
          <p className="mt-5 text-platinum text-lg max-w-2xl">
            Enjoy convenient, flavorful Jamaican-inspired meals prepared to make your week easier. Every meal includes an entrée and up to two sides.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-semibold text-charcoal">
            <UtensilsCrossed className="w-4 h-4" /> Meals start at $15 per meal
          </div>
          <div data-testid="meal-prep-schedule-card" className="mt-8 flex flex-col sm:flex-row gap-6 rounded-2xl border border-gold/40 bg-white/5 backdrop-blur-sm p-6 w-fit">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-gold" />
              <div><div className="eyebrow text-gold">Order Deadline</div>
                <div className="text-white font-semibold">{settings.order_deadline || "Thursday at 11:59 PM"}</div></div>
            </div>
            <div className="hidden sm:block w-px bg-white/15" />
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-gold" />
              <div><div className="eyebrow text-gold">Fulfillment</div>
                <div className="text-white font-semibold">{settings.fulfillment_days || "Sunday and Monday"}</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How It Works" title="From Request to Fulfillment"
            subtitle="Submitting an order request does not confirm your order — we'll review everything and follow up." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((p) => (
              <div key={p.n} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="w-10 h-10 rounded-full bg-charcoal text-gold font-serif text-xl flex items-center justify-center">{p.n}</div>
                <h3 className="mt-4 font-serif text-xl text-charcoal">{p.t}</h3>
                <p className="mt-1 text-sm text-slate-600">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORDER FORM */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="order" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-9">
            {done ? <Confirmation orderId={done} /> : (
              <>
                {/* Progress */}
                <div data-testid="meal-prep-progress" className="mb-9">
                  <div className="flex items-center justify-between">
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex-1 flex items-center">
                        <div className="flex flex-col items-center">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition ${
                            i < step ? "bg-emerald text-white" : i === step ? "bg-gold text-charcoal" : "bg-slate-100 text-slate-400"}`}>
                            {i < step ? <Check className="w-4 h-4" /> : i + 1}
                          </div>
                          <span className={`mt-2 text-[11px] sm:text-xs font-medium ${i===step?"text-charcoal":"text-slate-400"}`}>{s}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i<step?"bg-emerald":"bg-slate-200"}`} />}
                      </div>
                    ))}
                  </div>
                </div>

                {/* STEP 0 — PACKAGE */}
                {step === 0 && (
                  <div data-testid="step-package">
                    <h2 className="font-serif text-2xl text-charcoal">1. Choose Your Package</h2>
                    <p className="mt-2 text-sm text-slate-500">Meal Prep starts at <strong className="text-charcoal">$15 per meal</strong>. Each meal includes an entrée and up to two sides.</p>
                    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                      {(packages.length ? packages : [{meals:4},{meals:8},{meals:12},{meals:16}]).map((p) => (
                        <button key={p.meals} data-testid={`package-option-${p.meals}`}
                          onClick={() => setOrder((o)=>({...o, order_type:"package", package_meals:p.meals}))}
                          className={`rounded-2xl border-2 p-6 text-center transition ${!isCustom && order.package_meals===p.meals?"border-gold bg-gold/5":"border-slate-200 hover:border-gold/50"}`}>
                          <div className="text-4xl font-serif font-semibold text-charcoal">{p.meals}</div>
                          <div className="text-sm text-slate-500">Meals</div>
                          <div className="mt-2 text-sm font-semibold text-gold">{p.price ? p.price : "Pricing at review"}</div>
                        </button>
                      ))}
                    </div>
                    <button data-testid="package-option-custom"
                      onClick={() => setOrder((o)=>({...o, order_type:"custom", package_meals:null}))}
                      className={`mt-4 w-full rounded-2xl border-2 p-6 text-left transition ${isCustom?"border-gold bg-gold/5":"border-slate-200 hover:border-gold/50"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-serif text-2xl text-charcoal">Single Meal / Custom Order</div>
                          <div className="text-sm text-slate-500 mt-1">Not looking for a standard package? Tell us exactly what you'd like and we'll review and price it for you.</div>
                        </div>
                        {isCustom && <Check className="w-6 h-6 text-gold shrink-0" />}
                      </div>
                    </button>
                  </div>
                )}

                {/* STEP 1 — BUILD MEALS or CUSTOM */}
                {step === 1 && isCustom && (
                  <div data-testid="step-custom">
                    <h2 className="font-serif text-2xl text-charcoal">2. Your Custom Request</h2>
                    <p className="mt-2 text-sm text-slate-500">Describe the meal(s) or single order you'd like. Include entrées, sides, quantities and any details. We'll review and provide final pricing.</p>
                    <textarea data-testid="custom-request-textarea" rows={5} className={`${inputCls} mt-4`} value={order.custom_request}
                      onChange={(e) => set("custom_request", e.target.value)} placeholder="e.g. 1 Jerk Salmon with rice & peas and steamed veg; 2 oxtail dinners…" />
                    <AddOns {...{ sides, appetizers, desserts, order, bumpMap, addSidePrice, dessertPrice }} />
                  </div>
                )}

                {step === 1 && !isCustom && (
                  <div data-testid="step-meals">
                    <h2 className="font-serif text-2xl text-charcoal">2. Build Your {order.package_meals} Meals</h2>
                    <p className="mt-2 text-sm text-slate-500">
                      Choose an entrée and up to <strong className="text-charcoal">two included sides</strong> for each meal (no extra charge). Set a quantity for each meal — your totals must reach your package size.
                    </p>

                    <div data-testid="meals-built-counter" className={`mt-4 sticky top-20 z-10 rounded-xl border p-3 flex flex-wrap items-center justify-between gap-2 ${builtCount>=requiredMeals?"border-emerald/40 bg-emerald/10":"border-gold/50 bg-gold/10"}`}>
                      <span className="text-sm font-semibold text-charcoal">{requiredMeals} Meals Selected</span>
                      <span className="text-sm font-semibold" data-testid="meals-built-count">
                        Meals built: <span className={builtCount>=requiredMeals?"text-emerald":"text-crimson"}>{builtCount} of {requiredMeals}</span>
                      </span>
                    </div>
                    {builtCount < requiredMeals && (
                      <p data-testid="meals-built-hint" className="mt-2 text-xs text-crimson">Add {requiredMeals - builtCount} more meal(s) to continue — you can also build more than {requiredMeals}.</p>
                    )}

                    <div className="mt-4 space-y-4">
                      {order.meals.map((m, i) => (
                        <div key={i} data-testid={`meal-card-${i}`} className="rounded-2xl border border-slate-200 p-5 bg-bone/50">
                          <div className="flex items-center justify-between mb-3">
                            <span className="eyebrow text-crimson">Meal {i + 1}</span>
                            {order.meals.length > 1 && (
                              <button data-testid={`remove-meal-${i}`} onClick={() => removeMeal(i)} className="text-crimson hover:bg-crimson/10 rounded-lg p-1.5"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <label className="block sm:col-span-2"><span className="text-sm font-medium text-charcoal">Entrée <span className="text-crimson">*</span></span>
                              <select data-testid={`meal-entree-${i}`} className={`${selCls} mt-1.5`} value={m.entree} onChange={(e) => setMeal(i, { entree: e.target.value })}>
                                <option value="">Select an entrée…</option>
                                {entrees.map((e) => <option key={e} value={e}>{e}</option>)}
                              </select>
                            </label>
                            <label className="block"><span className="text-sm font-medium text-charcoal">Included Side 1</span>
                              <select data-testid={`meal-side1-${i}`} className={`${selCls} mt-1.5`} value={m.side1} onChange={(e) => setMeal(i, { side1: e.target.value })}>
                                <option value="">Choose Side 1…</option>
                                {sides.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </label>
                            <label className="block"><span className="text-sm font-medium text-charcoal">Included Side 2</span>
                              <select data-testid={`meal-side2-${i}`} className={`${selCls} mt-1.5`} value={m.side2} onChange={(e) => setMeal(i, { side2: e.target.value })}>
                                <option value="">Choose Side 2 (optional)…</option>
                                {sides.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </label>
                          </div>
                          <div className="mt-3 flex items-center gap-3">
                            <span className="text-sm text-slate-600">Quantity of this exact meal</span>
                            <div className="flex items-center gap-2">
                              <button data-testid={`meal-qty-minus-${i}`} onClick={() => setMeal(i, { quantity: Math.max(1, m.quantity - 1) })} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                              <span data-testid={`meal-qty-${i}`} className="w-6 text-center font-semibold">{m.quantity}</span>
                              <button data-testid={`meal-qty-plus-${i}`} onClick={() => setMeal(i, { quantity: m.quantity + 1 })} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button data-testid="add-meal-button" onClick={addMeal}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-6 py-3 font-semibold text-charcoal hover:bg-charcoal hover:text-white transition">
                      <Plus className="w-4 h-4" /> Add Another Meal
                    </button>

                    <AddOns {...{ sides, appetizers, desserts, order, bumpMap, addSidePrice, dessertPrice }} />

                    <div className="mt-6">
                      <label className="text-sm font-medium text-charcoal">Don't see what you're looking for? Request additional dishes.</label>
                      <textarea data-testid="additional-dishes-textarea" rows={3} className={`${inputCls} mt-2`} value={order.custom_request}
                        onChange={(e) => set("custom_request", e.target.value)} placeholder="e.g. Ackee & saltfish, vegetarian curry…" />
                    </div>
                  </div>
                )}

                {/* STEP 2 — FULFILLMENT */}
                {step === 2 && (
                  <div data-testid="step-fulfillment">
                    <h2 className="font-serif text-2xl text-charcoal">3. Fulfillment</h2>
                    <div className="mt-6">
                      <span className="text-sm font-medium text-charcoal">Preferred Fulfillment Day</span>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        {["Sunday", "Monday"].map((d) => (
                          <button key={d} data-testid={`fulfillment-day-${d.toLowerCase()}`} onClick={() => set("fulfillment_day", d)}
                            className={`rounded-xl border-2 py-4 font-semibold transition ${order.fulfillment_day===d?"border-gold bg-gold/5 text-charcoal":"border-slate-200 text-slate-600"}`}>{d}</button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6">
                      <span className="text-sm font-medium text-charcoal">Preferred Fulfillment Method</span>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <button data-testid="fulfillment-method-delivery" onClick={() => set("fulfillment_method", "Delivery")}
                          className={`rounded-xl border-2 py-4 font-semibold flex items-center justify-center gap-2 transition ${order.fulfillment_method==="Delivery"?"border-gold bg-gold/5 text-charcoal":"border-slate-200 text-slate-600"}`}>
                          <Truck className="w-4 h-4" /> Delivery
                        </button>
                        <button data-testid="fulfillment-method-pickup" onClick={() => set("fulfillment_method", "Pickup")}
                          className={`rounded-xl border-2 py-4 font-semibold flex items-center justify-center gap-2 transition ${order.fulfillment_method==="Pickup"?"border-gold bg-gold/5 text-charcoal":"border-slate-200 text-slate-600"}`}>
                          <Store className="w-4 h-4" /> Pickup
                        </button>
                      </div>
                    </div>
                    {order.fulfillment_method === "Pickup" && (
                      <div data-testid="pickup-note" className="mt-5 rounded-xl bg-emerald/5 border border-emerald/30 p-4 text-sm text-slate-700">
                        Pickup details will be provided after your order has been confirmed.
                      </div>
                    )}
                    {order.fulfillment_method === "Delivery" && (
                      <div data-testid="delivery-fields" className="mt-6 grid sm:grid-cols-2 gap-4 animate-fade-in">
                        <label className="block sm:col-span-2"><span className="text-sm font-medium text-charcoal">Delivery Address <span className="text-crimson">*</span></span>
                          <input data-testid="delivery-address" className={`${inputCls} mt-1.5`} value={order.delivery.address} onChange={(e)=>set("delivery",{...order.delivery,address:e.target.value})} /></label>
                        <label className="block"><span className="text-sm font-medium text-charcoal">Apartment / Suite</span>
                          <input data-testid="delivery-apt" className={`${inputCls} mt-1.5`} value={order.delivery.apt} onChange={(e)=>set("delivery",{...order.delivery,apt:e.target.value})} /></label>
                        <label className="block"><span className="text-sm font-medium text-charcoal">City <span className="text-crimson">*</span></span>
                          <input data-testid="delivery-city" className={`${inputCls} mt-1.5`} value={order.delivery.city} onChange={(e)=>set("delivery",{...order.delivery,city:e.target.value})} /></label>
                        <label className="block"><span className="text-sm font-medium text-charcoal">ZIP Code <span className="text-crimson">*</span></span>
                          <input data-testid="delivery-zip" className={`${inputCls} mt-1.5`} value={order.delivery.zip} onChange={(e)=>set("delivery",{...order.delivery,zip:e.target.value})} /></label>
                        <label className="block"><span className="text-sm font-medium text-charcoal">Preferred Delivery Window</span>
                          <input data-testid="delivery-window" className={`${inputCls} mt-1.5`} placeholder="e.g. Sunday afternoon" value={order.delivery.window} onChange={(e)=>set("delivery",{...order.delivery,window:e.target.value})} /></label>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 3 — DETAILS + SUMMARY */}
                {step === 3 && (
                  <div data-testid="step-details">
                    <h2 className="font-serif text-2xl text-charcoal">4. Your Details</h2>
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                      <label className="block"><span className="text-sm font-medium text-charcoal">First Name <span className="text-crimson">*</span></span>
                        <input data-testid="order-first-name" className={`${inputCls} mt-1.5`} value={order.first_name} onChange={(e)=>set("first_name",e.target.value)} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">Last Name <span className="text-crimson">*</span></span>
                        <input data-testid="order-last-name" className={`${inputCls} mt-1.5`} value={order.last_name} onChange={(e)=>set("last_name",e.target.value)} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">Email <span className="text-crimson">*</span></span>
                        <input data-testid="order-email" type="email" className={`${inputCls} mt-1.5`} value={order.email} onChange={(e)=>set("email",e.target.value)} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">Phone <span className="text-crimson">*</span></span>
                        <input data-testid="order-phone" className={`${inputCls} mt-1.5`} value={order.phone} onChange={(e)=>set("phone",e.target.value)} /></label>
                    </div>
                    <div className="mt-4">
                      <label className="text-sm font-medium text-charcoal">Dietary Preferences / Allergies / Special Requests</label>
                      <textarea data-testid="order-dietary" rows={2} className={`${inputCls} mt-1.5`} value={order.dietary} onChange={(e)=>set("dietary",e.target.value)} />
                      <p className="mt-1.5 text-xs text-slate-500">While we make every effort to accommodate requests, please contact us directly regarding severe food allergies.</p>
                    </div>
                    <div className="mt-4">
                      <label className="text-sm font-medium text-charcoal">Additional Notes</label>
                      <textarea data-testid="order-notes" rows={2} className={`${inputCls} mt-1.5`} value={order.notes} onChange={(e)=>set("notes",e.target.value)} />
                    </div>

                    {/* SUMMARY */}
                    <div data-testid="order-summary" className="mt-6 rounded-2xl border border-slate-200 overflow-hidden">
                      <div className="bg-charcoal px-5 py-3 text-white font-serif text-lg">Order Summary</div>
                      <div className="p-5 space-y-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Package</span>
                          <span className="font-semibold text-charcoal">{isCustom ? "Single Meal / Custom Order" : `${order.package_meals} Meals${selectedPackage?.price ? ` · ${selectedPackage.price}` : ""}`}</span>
                        </div>
                        {!isCustom && (
                          <div className="flex justify-between">
                            <span className="text-slate-500">Total meals ordered</span>
                            <span className="font-semibold text-charcoal" data-testid="summary-total-meals">{builtCount} (min {requiredMeals})</span>
                          </div>
                        )}
                        {!isCustom && order.meals.filter((m)=>m.entree).map((m, i) => (
                          <div key={i} className="border-t border-slate-100 pt-3">
                            <div className="font-semibold text-charcoal">{m.entree} <span className="text-slate-400 font-normal">× {m.quantity}</span></div>
                            <div className="text-slate-500">Included sides: {[m.side1, m.side2].filter((s)=>s&&s!=="None").join(", ") || "None selected"}</div>
                          </div>
                        ))}
                        {isCustom && order.custom_request && (
                          <div className="border-t border-slate-100 pt-3"><div className="text-slate-500">Custom request:</div><div className="text-charcoal">{order.custom_request}</div></div>
                        )}
                        {Object.keys(order.additional_sides).length > 0 && (
                          <div className="border-t border-slate-100 pt-3">
                            <div className="font-semibold text-charcoal">Additional Sides <span className="text-slate-400 font-normal">($5 each, packaged separately)</span></div>
                            {Object.entries(order.additional_sides).map(([n, q]) => <div key={n} className="flex justify-between text-slate-600"><span>{n} × {q}</span><span>${q*addSidePrice}.00</span></div>)}
                          </div>
                        )}
                        {Object.keys(order.appetizers).length > 0 && (
                          <div className="border-t border-slate-100 pt-3">
                            <div className="font-semibold text-charcoal">Appetizers <span className="text-slate-400 font-normal">(additional — Price TBD)</span></div>
                            {Object.entries(order.appetizers).map(([n, q]) => <div key={n} className="flex justify-between text-slate-600"><span>{n} × {q}</span><span>Price TBD</span></div>)}
                          </div>
                        )}
                        {Object.keys(order.desserts).length > 0 && (
                          <div className="border-t border-slate-100 pt-3">
                            <div className="font-semibold text-charcoal">Desserts <span className="text-slate-400 font-normal">(optional add-on, $6 each)</span></div>
                            {Object.entries(order.desserts).map(([n, q]) => <div key={n} className="flex justify-between text-slate-600"><span>{n} × {q}</span><span>${q*dessertPrice}.00</span></div>)}
                          </div>
                        )}
                        <div className="border-t border-slate-200 pt-3 space-y-1">
                          {additionalSidesTotal > 0 && <div className="flex justify-between text-slate-600"><span>Additional sides</span><span>${additionalSidesTotal}.00</span></div>}
                          {dessertsTotal > 0 && <div className="flex justify-between text-slate-600"><span>Desserts</span><span>${dessertsTotal}.00</span></div>}
                          <div className="flex justify-between font-semibold text-charcoal"><span>Add-on subtotal</span><span data-testid="summary-addon-total">${additionalSidesTotal + dessertsTotal}.00</span></div>
                          <p className="text-xs text-slate-400 pt-1">Base package price and appetizer pricing are confirmed by Taste of Platinum at review. This is not a final invoice.</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 rounded-xl bg-bone border border-slate-200 p-4 text-sm text-slate-600">
                      Submitting this request does <strong>not</strong> confirm your order. We'll review your selections and follow up with final pricing, availability and payment instructions.
                    </div>
                  </div>
                )}

                {/* NAV */}
                <div className="mt-9 flex items-center justify-between gap-3">
                  <button data-testid="meal-prep-back" onClick={back} disabled={step===0}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-semibold text-slate-600 disabled:opacity-40">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button data-testid="meal-prep-next" onClick={next} disabled={!canNext()}
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gold">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button data-testid="meal-prep-submit" onClick={submit} disabled={submitting}
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-60">
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit Order Request"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function AddOns({ sides, appetizers, desserts, order, bumpMap, addSidePrice = 5, dessertPrice = 6 }) {
  const Section = ({ title, note, items, mapKey, testidPrefix }) => (
    <div className="mt-6">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-charcoal">{title}</span>
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{note}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {items.map((name) => {
          const q = order[mapKey][name] || 0;
          return (
            <div key={name} data-testid={`${testidPrefix}-${slug(name)}`}
              className={`flex items-center justify-between rounded-xl border p-3 ${q>0?"border-gold bg-gold/5":"border-slate-200"}`}>
              <span className="text-charcoal text-sm">{name}</span>
              <div className="flex items-center gap-2">
                <button aria-label={`decrease ${name}`} onClick={() => bumpMap(mapKey, name, -1)} disabled={q===0} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                <span data-testid={`${testidPrefix}-qty-${slug(name)}`} className="w-6 text-center font-semibold">{q}</span>
                <button aria-label={`increase ${name}`} onClick={() => bumpMap(mapKey, name, 1)} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="mt-8 border-t border-slate-100 pt-6">
      <Section title="Additional Sides" note={`Additional sides are $${addSidePrice} each and are packaged separately (these are not the two sides included with each meal).`}
        items={sides} mapKey="additional_sides" testidPrefix="additional-side" />
      <Section title="Appetizers" note="Appetizers are an additional-cost item — pricing determined separately (Price TBD)."
        items={appetizers} mapKey="appetizers" testidPrefix="appetizer" />
      <Section title="Desserts" note={`Desserts are optional add-ons — $${dessertPrice} each. Not included in the base meal package.`}
        items={desserts} mapKey="desserts" testidPrefix="dessert" />
    </div>
  );
}

function Confirmation({ orderId }) {
  return (
    <div data-testid="order-confirmation" className="text-center py-4">
      <CheckCircle2 className="w-16 h-16 text-emerald mx-auto" />
      <h2 className="mt-5 font-serif text-3xl text-charcoal">Your Meal Prep Request Has Been Received!</h2>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto">
        Thank you for choosing Taste of Platinum. Your request has been submitted and is currently pending review.
      </p>
      <div className="mt-5 inline-block rounded-full bg-crimson/10 text-crimson px-5 py-2 text-sm font-semibold">
        Your order is not yet confirmed.
      </div>
      <div className="mt-6 inline-block rounded-2xl bg-bone px-8 py-4">
        <span className="text-sm text-slate-500">Order ID</span>
        <div className="font-serif text-3xl text-charcoal" data-testid="order-id-display">{orderId}</div>
      </div>
      <div className="mt-6 max-w-xl mx-auto text-sm text-slate-600 space-y-2 text-left rounded-2xl border border-slate-200 p-5">
        <p>We'll review your meal selections, availability, fulfillment preferences, and final order details.</p>
        <p>You'll receive information regarding confirmation, final pricing, delivery or pickup details, and payment instructions.</p>
        <p>Your order is not confirmed until you receive confirmation from Taste of Platinum and applicable payment requirements have been satisfied.</p>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
        <Link to="/" className="inline-flex items-center justify-center rounded-full border border-slate-300 px-7 py-3 font-semibold text-charcoal">Return Home</Link>
        <a href="https://instagram.com/theplatinumgrill" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full bg-charcoal px-7 py-3 font-semibold text-white">Follow @theplatinumgrill</a>
      </div>
    </div>
  );
}
